import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import GetCookie from './GetCookie';

const PermissionsContext = createContext(null);

const STALE_MS = 60 * 1000;
// A denial within this long of a fetch is not a sign the flags are stale.
const DENIED_REFETCH_GAP_MS = 5 * 1000;

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [email, setEmail] = useState(null);
  const lastFetchedAt = useRef(0);
  const inFlight = useRef(false);
  const location = useLocation();
  const isAdminConsole = location.pathname.startsWith('/admin');

  const fetchPermissions = useCallback(async (force = false) => {
    if (inFlight.current) return;
    if (!force && Date.now() - lastFetchedAt.current < STALE_MS) return;

    inFlight.current = true;
    try {
      const response = await axios.post(
        '/pceapi/general/profile-details',
        { console_type: 'iam-console' },
        { headers: { Authorization: GetCookie('access_token') } }
      );
      const data = response.data;
      if (data.status === 'success' && data.data) {
        setPermissions(data.data.permissions || {});
        setEmail(data.data.email || null);
        lastFetchedAt.current = Date.now();
      } else {
        setPermissions({});
        setEmail(null);
        lastFetchedAt.current = 0;
      }
    } catch (error) {
      setPermissions({});
      setEmail(null);
      lastFetchedAt.current = 0;
      console.error('Error fetching permissions:', error);
    } finally {
      inFlight.current = false;
    }
  }, []);

  // Route changes are the main trigger: login navigates client-side, so this
  // provider never remounts after it, and a page visit is exactly when the
  // answer is needed. STALE_MS keeps rapid navigation from refetching.
  useEffect(() => {
    if (isAdminConsole) return;
    fetchPermissions();
  }, [location.pathname, isAdminConsole, fetchPermissions]);

  // No refetch on window focus: that fired on every return to the tab (even a
  // click back from DevTools), mostly in the middle of unrelated work like a
  // quick-feedback run, and the backend checks access on every request anyway.
  // Instead, a request the backend turns down as ACCESS_DENIED — the moment the
  // flags are known to be out of date — refreshes them straight away, on every
  // screen. 
  useEffect(() => {
    if (isAdminConsole) return undefined;
    const interceptorId = axios.interceptors.response.use((response) => {
      const denied = response && response.data && response.data.code === 'ACCESS_DENIED';
      const url = (response && response.config && response.config.url) || '';
      if (denied && !url.includes('/general/profile-details')
          && Date.now() - lastFetchedAt.current > DENIED_REFETCH_GAP_MS) {
        fetchPermissions(true);
      }
      return response;
    });
    return () => axios.interceptors.response.eject(interceptorId);
  }, [isAdminConsole, fetchPermissions]);

  const can = useCallback((flag) => permissions?.[flag] === true, [permissions]);

  // Explicit user intent ("give me the latest") — skips the staleness window.
  const refreshPermissions = useCallback(() => fetchPermissions(true), [fetchPermissions]);

  const value = { permissions, email, can, refreshPermissions };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Hook to access permission gating.
 *
 * @returns {{ permissions: Object<string, boolean>, email: string|null, can: (flag: string) => boolean, refreshPermissions: () => void }}
 */
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    return { permissions: {}, email: null, can: () => false, refreshPermissions: () => {} };
  }
  return context;
};

export default PermissionsContext;
